<template>
  <div class="vm-container login-container">
    <div class="auth__content login__box">
      <a
        v-if="!submitted"
        class="back-link"
        @click="goToLogin()"
      >
        <font-awesome-icon
          :icon="['fas', 'chevron-left']"
          size="1x"
        />
        {{ $t('ManageIdentity.Views.Account.ForgotPasswordConfirmation.Login') }}
      </a>
      <br />
      <h1
        class="login__box__header"
        v-html="$t('ManageIdentity.Views.Account.Register.Headline')"
      />
      <div
        class="login__box__description"
        v-html="$t('ManageIdentity.Views.Account.Register.HeadlineClaim')"
      />
      <br />
      <b-alert
        :show="dismissCountDownInvalid"
        dismissible
        variant="danger"
        @dismissed="dismissCountDownInvalid=0"
        @dismiss-count-down="countDownChangedInvalid"
      >
        <div v-html="alertData" />
      </b-alert>
      <br />
      <b-alert
        :show="dismissCountDownSuccess"
        dismissible
        variant="success"
        @dismissed="dismissCountDownSuccess=0"
        @dismiss-count-down="countDownSuccess"
      >
        <h1 v-html="$t('ManageIdentity.SharedResources.StatusMessageSuccess')" />
        <span v-html="$t('ManageIdentity.Views.Account.ConfirmationSend.EmailInUse')" />
      </b-alert>
      <br />
      <b-alert
        :show="dismissCountDownConfirmation"
        dismissible
        variant="success"
        @dismissed="dismissCountDownConfirmation=0"
        @dismiss-count-down="countDownConfirmation"
      >
        <h1 v-html="$t('ManageIdentity.Views.Account.ConfirmationSend.ConfirmationSendHeadline')" />
        <span v-html="$t('ManageIdentity.Views.Account.ConfirmationSend.ConfirmationSendText')" />
      </b-alert>
      <br />
      <template v-if="submitted">
        <b-button
          class="login__box__login_btn"
          variant="success"
          @click="loginUser(responseData.token, false, responseData.returnUrl, 0, returnWithToken)"
        >
          {{ $t('ManageIdentity.Views.Account.Register.btnContinue') }}
        </b-button>
      </template>
      <template v-if="!submitted">
        <b-form-group
          id="fieldset-1"
          :label="email"
          label-for="input-1"
        >
          <b-form-input
            :disabled="submitted"
            id="input-1"
            v-model="credentials.Email"
            :placeholder="emailPlaceholder"
            trim
            type="email"
          />
        </b-form-group>
        <b-alert
          :show="!emailState"
          variant="danger"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.ErrorEmailRequired')" />
        </b-alert>
        <b-form-group
          id="fieldset-6"
          :label="$t('ManageIdentity.Views.Account.Register.ConfirmEmail')"
          label-for="input-6"
        >
          <b-form-input
            :disabled="submitted"
            id="input-6"
            v-model="emailConfirm"
            :placeholder="emailPlaceholder"
            trim
            type="email"
            :state="emailConfirmState && emailIdentical"
          />
        </b-form-group>
        <b-form-group
          id="fieldset-2"
          :label="firstName"
          label-for="input-2"
        >
          <b-form-input
            :disabled="submitted"
            id="input-2"
            v-model="credentials.Firstname"
            trim
            type="text"
          />
        </b-form-group>
        <b-alert
          :show="!firstNameState"
          variant="danger"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.ErrorFirstnameRequired')" />
        </b-alert>
        <b-form-group
          id="fieldset-3"
          :label="lastName"
          label-for="input-3"
        >
          <b-form-input
            id="input-3"
            :disabled="submitted"
            v-model="credentials.Surname"
            trim
            type="text"
          />
        </b-form-group>
        <b-alert
          :show="!lastNameState"
          variant="danger"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.ErrorSurnameRequired')" />
        </b-alert>
        <b-alert
          :show="passwordInfo"
          dismissible
          variant="primary"
          @dismissed="passwordInfo=false"
        >
          <h6 v-html="$t('ManageIdentity.Views.Account.Register.PasswordHintHeading')" />
          <span v-html="$t('ManageIdentity.Views.Account.Register.SubHeadline')" />
        </b-alert>
        <b-form-group>
          <slot
            id="fieldset-4"
            label-for="input-4"
            name="label"
          >
            <span
              class="labelIcon"
            >
              {{ password }}
              <font-awesome-icon
                :icon="['fas', 'info-circle']"
                class="faIcon"
                @click="openPasswordInfo()"
              />
            </span>
          </slot>
          <div class="password-input-container">
            <b-form-input
              v-if="showPassword"
              id="input-4"
              :disabled="submitted"
              v-model="credentials.Password"
              :placeholder="passwordPlaceholder"
              :state="passwordState"
              trim
              type="text"
            />
            <b-form-input
              v-else
              id="input-4"
              :disabled="submitted"
              v-model="credentials.Password"
              :placeholder="passwordPlaceholder"
              :state="passwordState"
              trim
              type="password"
            />
            <div>
              <button v-if="showPassword" class="btn btn-primary" @click="toggleShow">
                <font-awesome-icon :icon="['fas', 'eye-slash']" />
              </button>
              <button v-else class="btn btn-primary" @click="toggleShow">
                <font-awesome-icon :icon="['fas', 'eye']" />
              </button>
            </div>
          </div>
        </b-form-group>
        <b-form-group
          id="fieldset-5"
          :label="confirmPassword"
          label-for="input-5"
        >
          <div class="password-input-container">
            <b-form-input
              v-if="showPasswordConfirm"
              id="input-5"
              :disabled="submitted"
              v-model="credentials.ConfirmPassword"
              :placeholder="passwordPlaceholder"
              :state="confirmPasswordState && passwordIdentical"
              trim
              type="text"
            />
            <b-form-input
              v-else
              id="input-5"
              :disabled="submitted"
              v-model="credentials.ConfirmPassword"
              :placeholder="passwordPlaceholder"
              :state="confirmPasswordState && passwordIdentical"
              trim
              type="password"
            />
            <div>
              <button v-if="showPasswordConfirm" class="btn btn-primary" @click="toggleShowConfirm">
                <font-awesome-icon :icon="['fas', 'eye-slash']" />
              </button>
              <button v-else class="btn btn-primary" @click="toggleShowConfirm">
                <font-awesome-icon :icon="['fas', 'eye']" />
              </button>
            </div>
          </div>
        </b-form-group>
        <b-alert
          :show="credentials.Password.length && !passwordState"
          variant="danger"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.ErrorRequirementPassword')" />
        </b-alert>
        <b-alert
          :show="passwordState && !passwordIdentical"
          variant="danger"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.ErrorConfirmPassword')" />
        </b-alert>
        <b-form-checkbox
          id="checkbox-1"
          v-model="credentials.AcceptTermsOfUse"
          name="checkbox-1"
          :disabled="submitted"
        >
          <span>
            <a
              href="javascript:void(0)"
              @click="openModal(false)"
            >
              <span>{{ $t('ManageIdentity.Views.Account.Register.AcceptTerms') }}</span></a>
          </span>
        </b-form-checkbox>
        <br />
        <b-button
          :disabled="
            !emailState ||
              !emailConfirmState ||
              !emailIdentical ||
              !passwordState ||
              !firstNameState ||
              !lastNameState ||
              !termsState ||
              !confirmPasswordState ||
              !passwordIdentical ||
              submitted"
          class="login__box__login_btn"
          variant="primary"
          @click.prevent="handleAccountCreation()"
        >
          <span v-html="$t('ManageIdentity.Views.Account.Register.btnRegister')" />
        </b-button>
      </template>
      <modal
        :show="termsModal.show"
        @close="termsModal.show = false"
      >
        <b-card
          class="terms-card"
        >
          <div
            class="terms-card__close-button"
            @click.prevent="termsModal.show = false"
          >
            &times;
          </div>
          <b-card-title>
            {{ $t(termsModalTitle) }}
          </b-card-title>
          <b-card-text>{{ $t('ManageIdentity.Views.Account.Terms.SubHeadline') }}</b-card-text>
          <b-tabs card>
            <b-tab
              :title="termsTitle"
              active
            >
              <b-card-text
                class="modal-text-scroll"
                v-html="tnc"
              />
            </b-tab>
            <b-tab :title="dataProtectionTitle">
              <b-card-text
                class="modal-text-scroll"
                v-html="data"
              />
            </b-tab>
            <template v-if="getConfig.context === 'Event'">
              <b-tab
                :title="termsTitleOrg"
                active
                v-if="tncOrg"
              >
                <b-card-text
                  class="modal-text-scroll"
                  v-html="tncOrg"
                />
              </b-tab>
              <b-tab :title="dataProtectionTitleOrg" v-if="dataOrg">
                <b-card-text
                  class="modal-text-scroll"
                  v-html="dataOrg"
                />
              </b-tab>
            </template>
          </b-tabs>
          <b-button
            variant="success"
            @click.prevent="termsModal.show = false"
          >
            {{ $t('ManageIdentity.Views.Account.Register.btnClose') }}
          </b-button>
          <!--          <b-form-checkbox-->
          <!--            id="checkbox-1"-->
          <!--            v-model="credentials.AcceptTermsOfUse"-->
          <!--            name="checkbox-1"-->
          <!--          >-->
          <!--            <span>{{ $t('ManageIdentity.Views.Account.Register.AcceptTerms') }}</span>-->
          <!--          </b-form-checkbox>-->
        </b-card>
      </modal>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueCookies from 'vue-cookies'

import accountPageMixins from '../../mixins/accountPageMixins'
import userState from '../../states/user'
import { mapGetters, mapActions } from 'vuex'
import * as constants from '../../constants/constants'
Vue.use(VueCookies)

export default {
  data() {
    return {
      credentials: {
        Email: '',
        Firstname: '',
        Surname: '',
        Password: '',
        ConfirmPassword: '',
        AcceptTermsOfUse: false,
      },
      emailConfirm: '',
      passwordInfo: true,
      showPassword: false,
      showPasswordConfirm: false,
      dismissSecs: 60,
      dismissCountDownInvalid: 0,
      dismissCountDownConfirmation: 0,
      dismissCountDownSuccess: 0,
      submitted: false,
      responseData: {
        token: '',
        returnUrl: '',
      }
    }
  },
  props: {
    instance: {
      type: String,
      default: ''
    },
    returnWithToken: {
      type: Boolean,
      default: false
    }
  },
  mixins: [accountPageMixins],
  async mounted() {
    await this.loadTermsAndConditions()
    const externalAuthCode = Vue.$cookies.get('externalAuthorizationCode') ? Vue.$cookies.get('externalAuthorizationCode') : null
    if(externalAuthCode) {
      await this.loadEventInformation().then(async () => {
        await this.loadConfig().then(async () => {
          const prefillExternalUser = 'prefillExternalUser' in this.$store.getters['config/configForKey']('app_config').config[0].defaults ?this.$store.getters['config/configForKey']('app_config').config[0].defaults.prefillExternalUser : null
          if(prefillExternalUser) {
            await this.prefillUser().then((info) => {
              this.credentials.Email = info.Email
              this.emailConfirm = info.Email
              this.credentials.Firstname = info.Firstname
              this.credentials.Surname = info.Surname
            })
          }
      })
    })
  }
  },
  computed: {
    ...mapGetters({
      getConfig: 'ModuleConfig/moduleConfig',
    }),
    emailRegex() {
      return constants.emailRegex
    },
    emailState() {
      return this.credentials.Email && this.emailRegex.test(this.credentials.Email)
    },
    emailConfirmState() {
      return this.emailConfirm.length > 0 && this.emailRegex.test(this.emailConfirm)
    },
    nameRegex() {
      return constants.nameRegex
    },
    firstNameState() {
      return this.credentials.Firstname.length !== 0 || this.nameRegex.test(this.credentials.Firstname)
    },
    lastNameState() {
      return this.credentials.Surname.length !== 0 || this.nameRegex.test(this.credentials.Surname)
    },
    passwordRegex() {
      return constants.passwordRegex
    },
    passwordState() {
      return this.passwordRegex.test(this.credentials.Password)
    },
    confirmPasswordState() {
      return this.passwordRegex.test(this.credentials.ConfirmPassword)
    },
    passwordIdentical() {
      return this.credentials.Password === this.credentials.ConfirmPassword
    },
    emailIdentical() {
      return this.credentials.Email === this.emailConfirm
    },
    termsState() {
      return this.credentials.AcceptTermsOfUse
    },
    email() {
      return this.$t('ManageIdentity.Views.Account.Register.Email')
    },
    password() {
      return this.$t('ManageIdentity.Views.Account.Register.Password')
    },
    firstName() {
      return this.$t('ManageIdentity.Views.Account.Register.Firstname')
    },
    lastName() {
      return this.$t('ManageIdentity.Views.Account.Register.Surname')
    },
    confirmPassword() {
      return this.$t('ManageIdentity.Views.Account.Register.ConfirmPassword')
    },
    emailPlaceholder() {
      return this.$t('ManageIdentity.Views.Account.Register.Email')
    },
    passwordPlaceholder() {
      return this.$t('ManageIdentity.Views.Account.Register.PasswordPlaceholder')
    },
    termsModalTitle() {
      return this.$t('ManageIdentity.Views.Account.Terms.Headline')
    },
    termsTitle() {
      return this.$t('ManageIdentity.Views.Account.Register.Terms')
    },
    dataProtectionTitle() {
      return this.$t('ManageIdentity.Views.Account.Register.Data')
    },
    termsTitleOrg() {
      return this.$t('ManageIdentity.Views.Account.Register.TermsCustom')
    },
    dataProtectionTitleOrg() {
      return this.$t('ManageIdentity.Views.Account.Register.DataCustom')
    }
  },
  methods: {
    ...mapActions({
      loadConfig: 'config/loadConfig',
    }),
    async handleAccountCreation() {
      this.dismissCountDownLockout = 0
      this.dismissCountDownInvalid = 0
      this.dismissCountDownLockout = 0
      this.dismissCountDownConfirmation = 0

      await this.createAccount(this.credentials).then((res) => {
        if (res.status === 400) this.showInvalidAlert(res.status)
        if (res.status === 403) {
          this.showInvalidAlert(res.status)
          window.scrollTo(0, 0)
        }
        if (res.token) {
          this.submitted = true
          this.showConfirmationAlert()
          window.scrollTo(0, 0)
          this.responseData.token = res.token
          this.responseData.returnUrl = res.redirectUrl
          userState.setUser(this.instance || this.getConfig.instance, {
            accessToken: res.token,
            validUntil: new Date().getTime() + (86400 * 1000), // 3600 = 1h,
          })
          setTimeout(() => {
            this.loginUser(res.token, false, res.redirectUrl,0, this.returnWithToken)
          }, this.dismissSecs * 1000)
        }
      })
    },
    async openModal(params) {
      this.termsModal.show = true
      if (params) {
        this.loadTermsAndConditions()
        this.credentials.AcceptTermsOfUse = false
      }
    },
    countDownChangedInvalid(dismissCountDown) {
      this.dismissCountDownInvalid = dismissCountDown
    },
    countDownConfirmation(dismissCountDown) {
      this.dismissCountDownConfirmation = dismissCountDown
    },
    countDownSuccess(dismissCountDown) {
      this.dismissCountDownSuccess = dismissCountDown
    },
    openPasswordInfo() {
      this.passwordInfo = true
    },
    showConfirmationAlert() {
      this.dismissCountDownConfirmation = this.dismissSecs
    },
    showSuccessAlert() {
      this.dismissCountDownSuccess = this.dismissSecs
    },
    showInvalidAlert(params) {
      if (params === 400) this.alertData = this.badrequestText
      if (params === 403) this.alertData = this.accountExistText
      this.dismissCountDownInvalid = this.dismissSecs
    },
    toggleShow() {
      this.showPassword = !this.showPassword
    },
    toggleShowConfirm() {
      this.showPasswordConfirm = !this.showPasswordConfirm
    }
  },
}
</script>

<style lang="scss" scoped>
.labelIcon {
  display: flex;
  justify-content: space-between;

  .faIcon {
    cursor: pointer;
  }
}

.login-container {
  display: flex;
  justify-content: center;

  .login__box__header {
    text-align: center;
  }

  .login__box {
    display: flex;
    justify-content: center;
    flex-direction: column;

    .login__box__login_btn {
      max-width: 200px;
    }

    .login__box__login_btn:disabled {
      background-color: rgb(37, 37, 37);
      border-color: rgb(80, 80, 80);
      max-width: 200px;
    }


    .login__box__header, .login__box__description {
      display: flex;
      justify-content: center;
    }

    .login__box__description__two {
      display: flex;
    }

    .login__box__checkbox_link {
      display: flex;
      justify-content: space-between;
    }
  }
}

@media (max-width: 668px) {
  .login__box__checkbox_link {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.auth__content {
  width: 100vw;
  max-width: 800px;
  margin-top: 10px;
  padding: 15px;
}

.terms-card {
  border-radius: 10px;
}

.modal-text-scroll {
  overflow-y: scroll;
  height: 100vh;
  max-height: 45vh;
}

.terms-card__close-button {
  float: right;
  font-size: 2rem;
  margin-top: -20px;
  cursor: pointer;
}

</style>

<style lang="scss">
.login__box__header {
  font-size: 24px;
  font-weight: 700;
}

.back-link {
  cursor: pointer;
}

.password-input-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }
  .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    svg{
      fill: #fff;
      width: .875rem;
    }
  }
}

</style>
